import { format, startOfDay, subDays } from "date-fns";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Track } from "../types";
import { ListenerType, calculateListenerType } from "../utils/listenerTypes";
import { useLocalLibraryStore } from "./localLibraryStore";

interface PlayEvent {
  trackId: string;
  trackData: Track;
  timestamp: number;
  duration: number;
  skipped: boolean;
  source: "spotify" | "local";
}

interface SessionData {
  startTime: number;
  endTime: number;
  tracks: string[];
}

interface StatsState {
  playHistory: PlayEvent[];
  sessions: SessionData[];
  totalPlayTime: number;
  streaks: {
    current: number;
    longest: number;
    lastPlayDate: string | null;
  };

  // Track playback methods
  addPlayEvent: (track: Track, duration: number, skipped: boolean) => void;
  startSession: () => void;
  endSession: () => void;

  // Analysis methods
  getPlayTimeByPeriod: (period: "day" | "week" | "month" | "year") => number;
  getMostPlayedTracks: (limit?: number) => Array<{ track: Track; count: number }>;
  getMostPlayedArtists: (limit?: number) => Array<{ artist: string; count: number }>;
  getActiveHours: () => Record<number, number>;
  getListeningTrendsByDay: () => number[];
  getListeningTrendsByMonth: () => number[];
  getSourceDistribution: () => { spotify: number; local: number };
  getUniqueTracksPlayed: () => number;
  getUniqueArtistsPlayed: () => number;
  getAverageTrackDuration: () => number;
  getSkipRate: () => number;
  getMostActiveDay: () => { date: number; playCount: number };
  getCurrentStreak: () => number;
  getLongestStreak: () => number;
  getAverageSessionDuration: () => number;
  getLikedSongsCount: () => number;
  getTopArtist: () => { name: string; playCount: number } | null;
  getTopTrack: () => Track | null;

  // Listener profile methods
  getListenerMetrics: () => {
    skipRate: number;
    uniqueArtistsRatio: number;
    repeatPlayRate: number;
    dailyListeningTime: number;
    genreDiversity: number;
  };
  getListenerType: () => ListenerType;
}

export const useStatsStore = create<StatsState>()(
  persist(
    (set, get) => ({
      playHistory: [],
      sessions: [],
      totalPlayTime: 0,
      streaks: {
        current: 0,
        longest: 0,
        lastPlayDate: null,
      },

      addPlayEvent: (track, duration, skipped) => {
        set((state) => {
          const event: PlayEvent = {
            trackId: track.id,
            trackData: track,
            timestamp: Date.now(),
            duration,
            skipped,
            source: track.source,
          };

          // Update streaks
          const today = format(new Date(), "yyyy-MM-dd");
          const { streaks } = state;
          let newStreak = streaks.current;

          if (streaks.lastPlayDate !== today) {
            if (
              streaks.lastPlayDate &&
              format(new Date(streaks.lastPlayDate), "yyyy-MM-dd") === format(subDays(new Date(), 1), "yyyy-MM-dd")
            ) {
              newStreak++;
            } else {
              newStreak = 1;
            }
          }

          // Add to recently played in localLibraryStore
          useLocalLibraryStore.getState().addToRecentlyPlayed(track);

          return {
            playHistory: [...state.playHistory, event],
            totalPlayTime: state.totalPlayTime + duration,
            streaks: {
              current: newStreak,
              longest: Math.max(newStreak, streaks.longest),
              lastPlayDate: today,
            },
          };
        });
      },

      startSession: () => {
        set((state) => ({
          sessions: [
            ...state.sessions,
            {
              startTime: Date.now(),
              endTime: 0,
              tracks: [],
            },
          ],
        }));
      },

      endSession: () => {
        set((state) => ({
          sessions: state.sessions.map((session, index) =>
            index === state.sessions.length - 1 ? { ...session, endTime: Date.now() } : session
          ),
        }));
      },

      getPlayTimeByPeriod: (period) => {
        const state = get();
        const now = Date.now();
        const periodStart = {
          day: startOfDay(now).getTime(),
          week: subDays(now, 7).getTime(),
          month: subDays(now, 30).getTime(),
          year: subDays(now, 365).getTime(),
        }[period];

        return state.playHistory
          .filter((event) => event.timestamp >= periodStart)
          .reduce((total, event) => total + event.duration, 0);
      },

      getMostPlayedTracks: (limit = 10) => {
        const state = get();
        const trackCounts = state.playHistory.reduce((acc, event) => {
          acc[event.trackId] = (acc[event.trackId] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        return Object.entries(trackCounts)
          .map(([trackId, count]) => ({
            track: state.playHistory.find((e) => e.trackId === trackId)!.trackData,
            count,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, limit);
      },

      getMostPlayedArtists: (limit = 10) => {
        const state = get();
        const artistCounts = state.playHistory.reduce((acc, event) => {
          const artist = event.trackData.artist;
          acc[artist] = (acc[artist] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        return Object.entries(artistCounts)
          .map(([artist, count]) => ({ artist, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, limit);
      },

      getActiveHours: () => {
        const state = get();
        return state.playHistory.reduce((acc, event) => {
          const hour = new Date(event.timestamp).getHours();
          acc[hour] = (acc[hour] || 0) + event.duration;
          return acc;
        }, {} as Record<number, number>);
      },

      getListeningTrendsByDay: () => {
        const state = get();
        const dayTotals = new Array(7).fill(0);

        state.playHistory.forEach((event) => {
          const day = new Date(event.timestamp).getDay();
          dayTotals[day] += event.duration;
        });

        return dayTotals;
      },

      getListeningTrendsByMonth: () => {
        const state = get();
        const monthTotals = new Array(12).fill(0);

        state.playHistory.forEach((event) => {
          const month = new Date(event.timestamp).getMonth();
          monthTotals[month] += event.duration;
        });

        return monthTotals;
      },

      getSourceDistribution: () => {
        const state = get();
        const total = state.playHistory.length;
        if (total === 0) return { spotify: 0, local: 0 };

        const spotify = state.playHistory.filter((e) => e.source === "spotify").length;
        return {
          spotify: spotify / total,
          local: (total - spotify) / total,
        };
      },

      getUniqueTracksPlayed: () => {
        const state = get();
        return new Set(state.playHistory.map((e) => e.trackId)).size;
      },

      getUniqueArtistsPlayed: () => {
        const state = get();
        return new Set(state.playHistory.map((e) => e.trackData.artist)).size;
      },

      getAverageTrackDuration: () => {
        const state = get();
        if (!state.playHistory.length) return 0;
        return state.totalPlayTime / state.playHistory.length;
      },

      getSkipRate: () => {
        const state = get();
        if (!state.playHistory.length) return 0;
        return state.playHistory.filter((e) => e.skipped).length / state.playHistory.length;
      },

      getMostActiveDay: () => {
        const state = get();
        const dayCount = state.playHistory.reduce((acc, event) => {
          const day = startOfDay(event.timestamp).getTime();
          acc[day] = (acc[day] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);

        return Object.entries(dayCount).reduce(
          (max, [date, count]) => (count > max.playCount ? { date: Number(date), playCount: count } : max),
          { date: Date.now(), playCount: 0 }
        );
      },

      getCurrentStreak: () => {
        return get().streaks.current;
      },

      getLongestStreak: () => {
        return get().streaks.longest;
      },

      getAverageSessionDuration: () => {
        const state = get();
        const completedSessions = state.sessions.filter((s) => s.endTime > 0);
        if (!completedSessions.length) return 0;

        const totalDuration = completedSessions.reduce(
          (sum, session) => sum + (session.endTime - session.startTime),
          0
        );
        return totalDuration / completedSessions.length;
      },

      getLikedSongsCount: () => {
        return useLocalLibraryStore.getState().getLikedContent("track").length;
      },

      getTopArtist: () => {
        const artists = get().getMostPlayedArtists(1);
        return artists.length ? { name: artists[0].artist, playCount: artists[0].count } : null;
      },

      getTopTrack: () => {
        const tracks = get().getMostPlayedTracks(1);
        return tracks.length ? tracks[0].track : null;
      },

      getListenerMetrics: () => {
        const state = get();
        const last30Days = state.playHistory.filter((play) => play.timestamp > Date.now() - 30 * 24 * 60 * 60 * 1000);

        const skipRate = state.getSkipRate();

        const uniqueArtists = new Set(last30Days.map((play) => play.trackData.artist)).size;
        const uniqueArtistsRatio = uniqueArtists / Math.max(last30Days.length, 1);

        const trackCounts = last30Days.reduce((acc, play) => {
          acc[play.trackId] = (acc[play.trackId] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const repeatPlayRate =
          Object.values(trackCounts).filter((count) => count > 1).length / Math.max(Object.keys(trackCounts).length, 1);

        const dailyListeningTime = state.getPlayTimeByPeriod("day") / (24 * 60 * 60);

        const genreDiversity = Math.min(uniqueArtistsRatio * 1.5, 1);

        return {
          skipRate,
          uniqueArtistsRatio,
          repeatPlayRate,
          dailyListeningTime,
          genreDiversity,
        };
      },

      getListenerType: () => {
        return calculateListenerType(get().getListenerMetrics());
      },
    }),
    {
      name: "stats-storage",
      version: 1,
    }
  )
);
